/**
 * What the music says to a player who is not looking at the Music app: a cue as a notification across the
 * top of the phone, and each change of area spoken to a screen reader. Both belong to the phone's shell, so
 * a mail chime shows over Stats and an area change is announced over the Map.
 */
import { useMemo } from 'react';

import type { AudioCompanion } from './AudioCompanionProvider';
import { plainAreaName } from './parts';
import { cueLabel } from './prefs';

export function CueNotification({ companion }: { readonly companion: AudioCompanion }): React.JSX.Element | null {
  const cue = companion.state.lastCue;
  if (cue === null) return null;
  return (
    <p key={cue.at} className="au-cue" role="status" data-testid="cue-toast" data-cue={cue.id}>
      <span aria-hidden="true">♪</span> {cueLabel(cue.id)}
    </p>
  );
}

export function AreaAnnouncement({ companion }: { readonly companion: AudioCompanion }): React.JSX.Element {
  const { enabled, snapshot } = companion.state;
  const announcement = useMemo(() => {
    if (!enabled || snapshot.area === null) return '';
    const title = snapshot.track?.title;
    return `Now playing: ${plainAreaName(snapshot.area.displayName)}${title === undefined ? '' : `, ${title}`}`;
  }, [enabled, snapshot.area, snapshot.track]);
  return (
    <p className="dc-visually-hidden" aria-live="polite" data-testid="audio-announcement">
      {announcement}
    </p>
  );
}
